import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { ComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import keyBy from 'lodash/keyBy'
import reduce from 'lodash/reduce'
import { useMemo } from 'react'

import {
  ComputedSectionResult,
  evaluateComputedSectionWithOverride,
  resolveParameters,
} from './evaluateComputedSection'

export function useComputedSectionValue(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): ComputedSectionResult & {
  resolvedParams: Record<string, unknown>
  displayFieldValues: Record<string, unknown>
} {
  return useMemo(() => {
    const result = evaluateComputedSectionWithOverride(
      config,
      fieldValues,
      context
    )
    const resolvedParams = resolveParameters(config, fieldValues)

    const paramSpecsByField = keyBy(config.functionSpec.parameters, 'field')

    const fieldsToDisplay =
      config.displayFields.length > 0
        ? config.displayFields
        : config.functionSpec.parameters.map((p) => p.field)

    const displayFieldValues = reduce(
      fieldsToDisplay,
      (acc, fieldId) => {
        const value = unwrapFieldValue(fieldValues[fieldId])

        if (value !== undefined && value !== null) {
          acc[fieldId] = value
        } else {
          const paramSpec = paramSpecsByField[fieldId]

          if (paramSpec?.defaultValue !== undefined) {
            acc[fieldId] = paramSpec.defaultValue
          } else {
            acc[fieldId] =
              paramSpec && 'type' in paramSpec && paramSpec.type === 'tri-state'
                ? 'low'
                : false
          }
        }

        return acc
      },
      {} as Record<string, unknown>
    )

    return { ...result, resolvedParams, displayFieldValues }
  }, [config, fieldValues, context])
}
